/**
 * nxDialogo -- avisos y preguntas DENTRO de la pagina, en vez de alert/confirm/prompt del navegador (deuda 1 de
 * BACKLOG-DEPURACION-P-T1, Ares #32644; regla de hierro del panel: nunca dialogos nativos).
 *
 * Por que: el dialogo del navegador bloquea toda la pagina, no se puede estilar (letra chica en el celular), no se
 * puede probar sin un navegador y en el kiosco congela la pantalla compartida.
 *
 *   avisar(texto, { tono })                          aviso flotante que se va solo (como avisoDeCierre)
 *   confirmar({ titulo, texto, aceptar, peligro })   -> Promise<boolean>
 *   pedirTexto({ titulo, texto, etiqueta, valor, opciones })   -> Promise<string|null>  (null = cancelo)
 *   mostrarSecreto({ titulo, texto, secreto })       -> Promise<void>  (una clave nueva, una sola vez, con Copiar)
 *
 * Todo el texto entra con textContent: nada de lo que llega de la API se interpreta como HTML. Escape cancela,
 * Enter en el campo acepta, el foco va al primer control y vuelve a donde estaba al cerrar. Letra de 14px o mas.
 *
 * @module presentation/shared/nxDialogo
 */

const C = { fondo: '#161b22', borde: '#30363d', texto: '#e6edf3', suave: '#8b949e', ok: '#238636', mal: '#da3633', enlace: '#58a6ff' };

function el(tag, estilo, texto) {
  const e = document.createElement(tag);
  if (estilo) e.style.cssText = estilo;
  if (texto !== undefined) e.textContent = texto;
  return e;
}

/** Un parrafo por linea del texto (los textos viejos venian con \n para el alert). */
function parrafos(texto) {
  const caja = el('div', 'display:grid;gap:6px;margin-top:6px');
  String(texto ?? '').split('\n').map((l) => l.trim()).filter(Boolean)
    .forEach((l) => caja.appendChild(el('p', `margin:0;font-size:14px;line-height:1.45;color:${C.texto}`, l)));
  return caja;
}

const boton = (texto, primario, peligro) => {
  const b = el('button', `font-size:14px;padding:8px 14px;border-radius:6px;cursor:pointer;border:1px solid ${primario ? (peligro ? C.mal : C.ok) : C.borde};`
    + `background:${primario ? (peligro ? C.mal : C.ok) : 'transparent'};color:${C.texto}`, texto);
  b.type = 'button';
  return b;
};

/**
 * Arma el dialogo y devuelve una promesa que se resuelve con lo que `armar` decida. `armar(caja, cerrar)` pinta el
 * contenido y llama cerrar(valor) para terminar; Escape y el fondo llaman cerrar(alCancelar).
 */
function abrir({ titulo, armar, alCancelar, cancelable = true }) {
  return new Promise((resolver) => {
    const antes = document.activeElement;
    const fondo = el('div', 'position:fixed;inset:0;z-index:10000;background:rgba(1,4,9,.72);display:flex;align-items:center;justify-content:center;padding:16px');
    fondo.dataset.nxDialogo = '';
    const caja = el('div', `width:100%;max-width:440px;max-height:calc(100vh - 32px);overflow:auto;box-sizing:border-box;background:${C.fondo};border:1px solid ${C.borde};border-radius:10px;padding:16px`);
    caja.setAttribute('role', 'dialog');
    caja.setAttribute('aria-modal', 'true');
    const idTitulo = 'nx-dlg-' + Math.random().toString(36).slice(2, 8);
    const h = el('h2', `margin:0;font-size:16px;color:${C.texto}`, titulo || '');
    h.id = idTitulo;
    caja.setAttribute('aria-labelledby', idTitulo);
    caja.appendChild(h);
    let hecho = false;
    const cerrar = (valor) => {
      if (hecho) return;
      hecho = true;
      document.removeEventListener('keydown', alTeclear, true);
      fondo.remove();
      if (antes && typeof antes.focus === 'function') antes.focus();
      resolver(valor);
    };
    // Q #32669: Tab y Shift+Tab dan la vuelta DENTRO del dialogo (el foco no se escapa a la pagina de atras).
    const enfocables = () => [...caja.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((x) => !x.disabled);
    const alTeclear = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); if (cancelable) cerrar(alCancelar); return; }
      if (e.key === 'Tab') {
        const lista = enfocables();
        if (!lista.length) return;
        const i = lista.indexOf(document.activeElement);
        const siguiente = e.shiftKey ? (i <= 0 ? lista.length - 1 : i - 1) : (i === -1 || i === lista.length - 1 ? 0 : i + 1);
        e.preventDefault();
        lista[siguiente].focus();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    // Q #32669: lo que no se puede perder (una clave de una sola vez) no se cierra con Escape ni con el fondo.
    fondo.addEventListener('click', (e) => { if (e.target === fondo && cancelable) cerrar(alCancelar); });
    armar(caja, cerrar);
    fondo.appendChild(caja);
    document.body.appendChild(fondo);
    const primero = caja.querySelector('input, select, button');
    if (primero) primero.focus();
  });
}

function pie(caja, botones) {
  const fila = el('div', 'display:flex;flex-wrap:wrap;gap:8px;justify-content:flex-end;margin-top:14px');
  botones.forEach((b) => fila.appendChild(b));
  caja.appendChild(fila);
}

/** Aviso flotante que se va solo. tono: info | ok | error. */
export function avisar(texto, { tono = 'info', ms = 6000 } = {}) {
  const aviso = document.createElement('div');
  aviso.className = 'reward-toast';
  aviso.dataset.nxAviso = tono;
  aviso.setAttribute('role', tono === 'error' ? 'alert' : 'status');
  aviso.style.borderColor = tono === 'error' ? '#f85149' : tono === 'ok' ? '#3fb950' : '#d29922';
  aviso.style.fontSize = '14px';
  aviso.textContent = texto;
  document.body.appendChild(aviso);
  requestAnimationFrame(() => aviso.classList.add('visible'));
  setTimeout(() => { aviso.classList.remove('visible'); setTimeout(() => aviso.remove(), 400); }, ms);
}

/** Pregunta si o no. El boton de aceptar dice lo que hace (no "OK"). */
export function confirmar({ titulo, texto = '', aceptar = 'Aceptar', cancelar = 'Cancelar', peligro = false } = {}) {
  return abrir({
    titulo, alCancelar: false,
    armar: (caja, cerrar) => {
      caja.appendChild(parrafos(texto));
      const no = boton(cancelar, false);
      const si = boton(aceptar, true, peligro);
      si.dataset.nxAceptar = '';
      no.addEventListener('click', () => cerrar(false));
      si.addEventListener('click', () => cerrar(true));
      pie(caja, [no, si]);
    }
  });
}

/** Pide un texto o una opcion. null = cancelo; '' = acepto vacio (el que llama decide el valor por defecto). */
export function pedirTexto({ titulo, texto = '', etiqueta = '', valor = '', opciones = null, aceptar = 'Continuar', cancelar = 'Cancelar' } = {}) {
  return abrir({
    titulo, alCancelar: null,
    armar: (caja, cerrar) => {
      caja.appendChild(parrafos(texto));
      const id = 'nx-dlg-campo-' + Math.random().toString(36).slice(2, 8);
      const lab = el('label', `display:block;margin-top:10px;font-size:14px;color:${C.suave}`, etiqueta);
      lab.htmlFor = id;
      let campo;
      if (Array.isArray(opciones)) {
        campo = el('select', `width:100%;box-sizing:border-box;margin-top:4px;font-size:14px;padding:8px;border-radius:6px;border:1px solid ${C.borde};background:#0d1117;color:${C.texto}`);
        opciones.forEach((o) => { const op = el('option', '', o.texto ?? o.valor); op.value = o.valor; campo.appendChild(op); });
        campo.value = valor;
      } else {
        campo = el('input', `width:100%;box-sizing:border-box;margin-top:4px;font-size:14px;padding:8px;border-radius:6px;border:1px solid ${C.borde};background:#0d1117;color:${C.texto}`);
        campo.value = valor;
      }
      campo.id = id;
      campo.dataset.nxCampo = '';
      campo.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); cerrar(String(campo.value)); } });
      caja.appendChild(lab);
      caja.appendChild(campo);
      const no = boton(cancelar, false);
      const si = boton(aceptar, true);
      si.dataset.nxAceptar = '';
      no.addEventListener('click', () => cerrar(null));
      si.addEventListener('click', () => cerrar(String(campo.value)));
      pie(caja, [no, si]);
    }
  });
}

/**
 * Una clave nueva que se ve UNA vez: con boton Copiar y Listo. No se escribe en ningun log. Solo se cierra con Listo
 * (Q #32669: con Escape o un clic afuera se perdia la clave).
 */
export function mostrarSecreto({ titulo, texto = '', secreto = '', copiar = '' } = {}) {
  return abrir({
    titulo, alCancelar: undefined, cancelable: false,
    armar: (caja, cerrar) => {
      caja.appendChild(parrafos(texto));
      const valor = el('code', `display:block;margin-top:10px;padding:10px;border-radius:6px;background:#0d1117;border:1px solid ${C.borde};font-size:15px;color:${C.texto};overflow-wrap:anywhere;user-select:all`, secreto);
      valor.dataset.nxSecreto = '';
      caja.appendChild(valor);
      const estado = el('div', `margin-top:6px;font-size:14px;color:${C.suave}`, '');
      estado.setAttribute('role', 'status');
      caja.appendChild(estado);
      const cop = boton('Copiar', false);
      const listo = boton('Listo', true);
      cop.addEventListener('click', async () => {
        try { await navigator.clipboard.writeText(copiar || secreto); estado.textContent = 'Copiado.'; }
        catch { estado.textContent = 'No se pudo copiar: seleccionalo y copialo a mano.'; }
      });
      listo.addEventListener('click', () => cerrar(undefined));
      pie(caja, [cop, listo]);
    }
  });
}
